/**
 * API Services para Configurações de Contexto de Mensagem
 * VigosCloud - Message Context System
 * 
 * Baseado no padrão incomingCallsApi.js
 * Todas as chamadas incluem autenticação via token
 */

import axios from 'axios';
import accessLocalStorage from './accessLocalStorage';

/**
 * Cria uma nova configuração de mensagem
 * @param {Object} configData - Dados da configuração
 * @returns {Promise} Response da API
 */
export const createMessageConfig = async (configData) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [MESSAGE-API] Criando configuração:', configData);
    console.log('🔍 [MESSAGE-API] URL:', `${ipRequest}api/config/message/configs`);
    console.log('🔍 [MESSAGE-API] Token:', token?.substring(0, 50) + '...');
    
    // Formatar dados para API
    const formattedData = formatConfigDataForAPI(configData);
    console.log('🔍 [MESSAGE-API] Dados formatados:', formattedData);
    
    const response = await axios.post(
      `${ipRequest}api/config/message/configs`,
      formattedData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [MESSAGE-API] Response status:', response.status);
    console.log('✅ [MESSAGE-API] Response data:', response.data);
    console.log('✅ [MESSAGE-API] Configuração criada:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro ao criar configuração:', error);
    console.error('❌ [MESSAGE-API] Error response status:', error.response?.status);
    console.error('❌ [MESSAGE-API] Error response data:', error.response?.data);
    console.error('❌ [MESSAGE-API] Error message:', error.message);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao criar configuração',
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Lista todas as configurações de mensagem
 * @param {string} tenant - Tenant
 * @returns {Promise} Response da API
 */
export const getAllMessageConfigs = async (tenant) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [MESSAGE-API] Listando configurações para tenant:', tenant);
    
    const response = await axios.get(
      `${ipRequest}api/config/message/configs?tenant=${tenant}`,
      { headers: { Authorization: token } }
    );
    
    console.log(`✅ [MESSAGE-API] Encontradas ${response.data.data?.length || 0} configurações`);
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro ao listar configurações:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao listar configurações',
      data: []
    };
  }
};

/**
 * Busca configuração de mensagem por ID
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getMessageConfigById = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [MESSAGE-API] Buscando configuração ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/message/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [MESSAGE-API] Configuração encontrada');
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro ao buscar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar configuração',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Atualiza configuração de mensagem
 * @param {number} id - ID da configuração
 * @param {Object} updateData - Dados para atualização
 * @returns {Promise} Response da API
 */
export const updateMessageConfig = async (id, updateData) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [MESSAGE-API] Atualizando configuração ID: ${id}`, updateData);
    
    // Formatar dados para API
    const formattedData = formatConfigDataForAPI(updateData);
    
    const response = await axios.put(
      `${ipRequest}api/config/message/configs/${id}`,
      formattedData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [MESSAGE-API] Configuração atualizada');
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro ao atualizar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao atualizar configuração',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Deleta configuração de mensagem
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const deleteMessageConfig = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [MESSAGE-API] Deletando configuração ID: ${id}`);
    
    const response = await axios.delete(
      `${ipRequest}api/config/message/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [MESSAGE-API] Configuração deletada');
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro ao deletar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao deletar configuração',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Debug: visualizar dialplan gerado
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getDialplanDebug = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [MESSAGE-API] Debug dialplan para ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/message/debug/dialplan/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [MESSAGE-API] Dialplan obtido');
    return response.data;
  } catch (error) {
    console.error('❌ [MESSAGE-API] Erro no debug:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro no debug',
      dialplan: ''
    };
  }
};

/**
 * Valida dados de configuração de mensagem antes de enviar
 * @param {Object} configData - Dados da configuração
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateMessageConfig = (configData) => {
  const errors = [];
  
  // Validar tenant
  if (!configData.tenant || configData.tenant.trim() === '') {
    errors.push('Tenant é obrigatório');
  }
  
  // Validar context_name
  if (!configData.context_name || configData.context_name.trim() === '') {
    errors.push('Nome do contexto é obrigatório');
  } else if (!/^msg-[a-z0-9-]+$/.test(configData.context_name)) {
    errors.push('Nome do contexto deve seguir o formato: msg-{nome}-{tenant} (apenas lowercase e hífen)');
  }
  
  // Validar áudio se habilitado
  if (configData.audio_enabled) {
    if (!configData.audio_file || configData.audio_file.trim() === '') {
      errors.push('Arquivo de áudio é obrigatório quando áudio está habilitado');
    }
    if (!configData.audio_type) {
      errors.push('Tipo de áudio é obrigatório quando áudio está habilitado');
    }
    if (!configData.audio_accountcode) {
      errors.push('AccountCode do áudio é obrigatório quando áudio está habilitado');
    }
  }
  
  // Validar destination_type
  if (!configData.destination_type) {
    errors.push('Tipo de destino é obrigatório');
  }
  
  const validDestinationTypes = ['endpoint', 'queue', 'external', 'disa', 'whatsapp', 'cycle', 'hangup'];
  if (configData.destination_type && !validDestinationTypes.includes(configData.destination_type)) {
    errors.push(`Tipo de destino inválido: ${configData.destination_type}`);
  }
  
  // Validar destino (obrigatório exceto para hangup, cycle e whatsapp)
  if (configData.destination_type && 
      !['hangup', 'cycle', 'whatsapp'].includes(configData.destination_type) && 
      (!configData.destination || configData.destination.trim() === '')) {
    errors.push('Destino é obrigatório para o tipo selecionado');
  }
  
  // Validar overflows
  if (configData.overflows && Array.isArray(configData.overflows)) {
    configData.overflows.forEach((overflow, index) => {
      if (!overflow.timeout_seconds || overflow.timeout_seconds < 1) {
        errors.push(`Overflow ${index + 1}: Timeout deve ser um número positivo`);
      }
      if (!overflow.destination_type) {
        errors.push(`Overflow ${index + 1}: Tipo de destino é obrigatório`);
      }
      if (!validDestinationTypes.includes(overflow.destination_type)) {
        errors.push(`Overflow ${index + 1}: Tipo de destino inválido`);
      }
      
      // Validar áudio do overflow se habilitado
      if (overflow.audio_enabled && !overflow.audio_file) {
        errors.push(`Overflow ${index + 1}: Arquivo de áudio é obrigatório quando áudio está habilitado`);
      }
    });
  }
  
  // Validar time conditions
  if (configData.gotoiftime_rules && Array.isArray(configData.gotoiftime_rules)) {
    configData.gotoiftime_rules.forEach((rule, index) => {
      if (!rule.time_condition || rule.time_condition.trim() === '') {
        errors.push(`Time Condition ${index + 1}: Condição de tempo é obrigatória`);
      }
      if (!rule.destination_type) {
        errors.push(`Time Condition ${index + 1}: Tipo de destino é obrigatório`);
      }
      if (!validDestinationTypes.includes(rule.destination_type)) {
        errors.push(`Time Condition ${index + 1}: Tipo de destino inválido`);
      }
    });
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Formata dados de configuração para envio à API
 * @param {Object} configData - Dados da configuração
 * @returns {Object} Dados formatados
 */
export const formatConfigDataForAPI = (configData) => {
  const formatted = {
    tenant: configData.tenant,
    context_name: configData.context_name,
    audio_enabled: configData.audio_enabled || false,
    destination_type: configData.destination_type
  };
  
  // Adicionar campos opcionais apenas se tiverem valor
  if (configData.description && configData.description.trim() !== '') {
    formatted.description = configData.description;
  }
  
  if (configData.audio_file) {
    formatted.audio_file = configData.audio_file;
  }
  
  if (configData.audio_type) {
    formatted.audio_type = configData.audio_type;
  }
  
  if (configData.audio_accountcode) {
    formatted.audio_accountcode = configData.audio_accountcode;
  }
  
  if (configData.destination && configData.destination.trim() !== '') {
    formatted.destination = configData.destination;
  }
  
  // Adicionar campos de MOH
  formatted.moh_enabled = configData.moh_enabled || false;
  if (configData.moh_class) {
    formatted.moh_class = configData.moh_class;
  }
  
  // Formatar overflows (apenas se não estiver vazio)
  if (configData.overflows && Array.isArray(configData.overflows) && configData.overflows.length > 0) {
    formatted.overflows = configData.overflows.map((overflow, index) => {
      const formattedOverflow = {
        timeout_seconds: parseInt(overflow.timeout_seconds),
        destination_type: overflow.destination_type,
        audio_enabled: overflow.audio_enabled || false,
        hangup_after_audio: overflow.hangup_after_audio || false,
        order_priority: overflow.order_priority || (index + 1)
      };
      
      if (overflow.destination_overflow) {
        formattedOverflow.destination_overflow = overflow.destination_overflow;
      }
      if (overflow.audio_file) {
        formattedOverflow.audio_file = overflow.audio_file;
      }
      if (overflow.audio_type) {
        formattedOverflow.audio_type = overflow.audio_type;
      }
      if (overflow.audio_accountcode) {
        formattedOverflow.audio_accountcode = overflow.audio_accountcode;
      }
      
      return formattedOverflow;
    });
  }
  
  // Formatar time conditions (apenas se não estiver vazio)
  if (configData.gotoiftime_rules && Array.isArray(configData.gotoiftime_rules) && configData.gotoiftime_rules.length > 0) {
    formatted.gotoiftime_rules = configData.gotoiftime_rules.map((rule, index) => {
      const formattedRule = {
        time_condition: rule.time_condition,
        destination_type: rule.destination_type,
        priority_order: rule.priority_order || (index + 1)
      };
      
      if (rule.destination) {
        formattedRule.destination = rule.destination;
      }
      
      return formattedRule;
    });
  }
  
  return formatted;
};

export default {
  createMessageConfig,
  getAllMessageConfigs,
  getMessageConfigById,
  updateMessageConfig,
  deleteMessageConfig,
  getDialplanDebug,
  validateMessageConfig,
  formatConfigDataForAPI
};

